import type { AnchorHTMLAttributes, ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

type Route = { path: string }

const RouterContext = createContext<{ route: Route; navigate: (path: string) => void } | null>(null)

export function RouterProvider({ children }: { children: ReactNode }) {
  const [route, setRoute] = useState<Route>({ path: window.location.pathname })

  useEffect(() => {
    function onPop() {
      setRoute({ path: window.location.pathname })
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  function navigate(path: string) {
    if (path === window.location.pathname) return
    window.history.pushState({}, '', path)
    setRoute({ path })
  }

  return <RouterContext.Provider value={{ route, navigate }}>{children}</RouterContext.Provider>
}

export function useRoute(): Route {
  const ctx = useContext(RouterContext)
  if (!ctx) throw new Error('useRoute must be inside <RouterProvider>')
  return ctx.route
}

export function useNavigate() {
  const ctx = useContext(RouterContext)
  if (!ctx) throw new Error('useNavigate must be inside <RouterProvider>')
  return ctx.navigate
}

export function Link({
  to,
  children,
  ...rest
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & { to: string; children: ReactNode }) {
  const navigate = useNavigate()
  return (
    <a
      href={to}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey) return
        e.preventDefault()
        navigate(to)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
